import { FlexBox, Icon, IconTypes, Text } from '@fable/components'
import { css, cx, useTheme } from '@fable/theme'
import { Fragment, useCallback, useEffect, useState } from 'react'
import { ReaderSummaryFeedbackForm } from './ReaderSummaryFeedbackForm'
import { StatsSummaryFeedbackType } from '../../../lib/types'
import { useStats } from '../../../lib/ctx/stats'
import { useReaderSummary } from '../../../lib/ctx/reader_summary'
import { OverflowMenu } from '../../shared'
import { useStatsTheme } from 'app/webview/pages/stats_profile/theme/ctx'

type Feedback = StatsSummaryFeedbackType
interface FeedbackItem {
  id: Feedback
  icon: IconTypes
}

export const ReaderSummaryFeedbackButtons = () => {
  const [open, setOpen] = useState(false)
  const isSelfView = useStats((s) => s.isSelfView)
  const [summaryFeedback, sendFeedback, isZeroState] = useReaderSummary((s) => [
    s.summaryFeedback,
    s.sendFeedback,
    s.isZeroState,
  ])
  const feedbackButtons: FeedbackItem[] = [
    {
      id: 'negative',
      icon: 'thumbs-down',
    },
    {
      id: 'positive',
      icon: 'thumbs-up',
    },
  ]

  if (isZeroState || !isSelfView) return null

  const handleSelectFeedback = (feedback: Feedback) => {
    if (feedback === 'positive') {
      sendFeedback({ positive: true, feedback: 'none' })
    } else {
      setOpen(!open)
    }
  }

  return (
    <FlexBox gap="8px">
      {feedbackButtons.map(({ id, icon }) => {
        const selected = summaryFeedback === id
        const button = (
          <FeebackButton
            id={id}
            selected={selected}
            onSelect={() => !selected && handleSelectFeedback(id)}
            icon={icon}
          />
        )

        return (
          <Fragment key={id}>
            {id === 'negative' ? (
              <OverflowMenu
                wrapperStyles={css`
                  top: 32px;
                  left: 50%;
                  transform: translateX(-50%) !important;
                `}
                containerStyles={css`
                  padding: 32px 12px 12px;
                `}
                open={open}
                setOpen={setOpen}
                trigger={button}
                hasCloseButton
                free
                disabled={selected}
              >
                <FeedbackNegativeOptions open={open} />
              </OverflowMenu>
            ) : (
              button
            )}
          </Fragment>
        )
      })}
    </FlexBox>
  )
}

export const FeebackButton = ({
  id,
  selected,
  onSelect,
  icon,
}: {
  id: Feedback
  selected: boolean
  onSelect: (id: Feedback) => void
  icon: IconTypes
}) => {
  const { colors } = useTheme()
  const theme = useStatsTheme((s) => s.theme)

  return (
    <FlexBox
      className={css`
        flex-shrink: 0;
        width: 32px;
        height: 32px;
        background-color: ${selected
          ? colors.uiWhite
          : 'rgba(255, 255, 255, 0.1)'};
        border-radius: 50%;
        transition: background-color 0.3s;
        cursor: pointer;
      `}
      gap={selected ? '0' : '8px'}
      alignItems="center"
      onClick={() => onSelect(id)}
      centerAll
    >
      <Icon
        className={css`
          svg {
            width: 14px;
          }
        `}
        fill={selected ? theme.colors.primary : colors.uiWhite}
        type={icon}
      />
    </FlexBox>
  )
}

export const FeedbackNegativeOptions = ({ open }: { open: boolean }) => {
  const { colors } = useTheme()
  const [submitted, setSubmitted] = useState(false)
  const containerStyles = css`
    width: 100%;
  `
  const icon = (
    <Icon
      type="info"
      fill={colors.uiPewterGrey}
      className={css`
        width: 17px;
        height: 17px;
        margin-bottom: 2px;
      `}
    />
  )

  const resetForm = useCallback(() => {
    const timeout = setTimeout(() => {
      if (!open) setSubmitted(false)
    }, 300)

    return () => clearTimeout(timeout)
  }, [open])

  useEffect(() => {
    resetForm()
  }, [resetForm])

  return submitted ? (
    <div
      className={cx(
        css`
          padding-bottom: 20px;
        `,
        containerStyles
      )}
    >
      {icon}
      <Text
        type="body"
        sizing="M"
        color={colors.uiWhite}
        className={css`
          p + p {
            margin-top: 16px;
          }
        `}
      >
        <p>
          <b>Thanks for helping us make this feature even better!</b>
          <br />
          <br />
          Since your Reader Summary didn’t work out, we’ve hidden it. We’ll give
          this another try when you finish your next book.
          <br />
          <br />
          If you have more feedback you’d like to share, please email us
          at support@fable.co.
        </p>
      </Text>
    </div>
  ) : (
    <div className={containerStyles}>
      {icon}
      <Text type="body" sizing="M" color={colors.uiWhite} weight="bold">
        What can we improve? Select all that apply:
      </Text>
      <ReaderSummaryFeedbackForm
        className={css`
          margin-top: 10px;
        `}
        onSubmit={() => setSubmitted(true)}
      />
    </div>
  )
}
